// Import temprano: inicializa el New Relic Browser agent apenas se evalúa el bundle.
import '@lib/newrelic/agent'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import '@styles/main.css'
import '@styles/chrome-bug.css'
import 'swiper/swiper-bundle.css'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import type { AppProps } from 'next/app'
import type { NextPage } from 'next'
import { appWithTranslation } from 'next-i18next/pages'
import nextI18nextConfig from '../next-i18next.config'
import {
    NanoProgress,
    useRouteProgressState
} from '@corratech/pylot-nano-progress'
import { ManagedUIContext } from '@corratech/pylot-ui/context'
import { ManagedAuthContext } from '@corratech/pylot-auth-manager'
import { Head } from '@components/common'
import { ScrollUpButton } from '@components/ScrollUpButton'
import { PopUpNewsFeatures } from '@components/PopUpNewsFeatures'
import { TagContextInit } from '@corratech/pylot-tag-manager'
import { useGTM } from '@corratech/pylot-gtm'
import { useAppErrorRecovery } from '@lib/hooks/useAppErrorRecovery'
import { useNewRelic } from '@lib/hooks/useNewRelic'
import { useBodyScrollLockReset } from '@lib/hooks/useBodyScrollLockReset'
import { ErrorBoundary } from '@components/ErrorBoundary/ErrorBoundary'
import { useMagentoStorefrontEvents } from '@lib/hooks/useMagentoStorefrontEvents'
import { useRegisterServiceWorker } from '@lib/hooks/useRegisterServiceWorker'
import { useSynonymDiscovery } from '@pylot-data/hooks/search/use-synonym-discovery'
import { LoadingIndicator } from '@components/LoadingIndicator'
import smoothscroll from 'smoothscroll-polyfill'
import { ModalStateProvider } from 'contexts/casa-ideas'
import { getGtmMappingOverrides } from '@lib/utils/gtm'
import { CardInfoProvider } from 'contexts/card-info'
import { DeviceProvider } from 'contexts/device-context'
import { resetIdCounter } from 'react-tabs'

type LayoutComponentProps = {
    children?: React.ReactNode
    pageProps?: AppProps['pageProps']
    authRequired?: boolean
}

type NextPageWithLayout = NextPage & {
    Layout?: React.ComponentType<LayoutComponentProps>
    Protected?: boolean
}

type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout
}

const Toast = dynamic(
    //@ts-ignore
    () => import('@corratech/pylot-ui').then((e) => e.Toast),
    { ssr: false }
)

const GlobalModal = dynamic(() => import('@components/common/Modal'))

import 'polyfill-object.fromentries'

const Noop = ({ children }: LayoutComponentProps) => <>{children}</>

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
    const router = useRouter()
    // react-tabs usa un contador de módulo. En el server sobrevive entre
    // requests y los id no coinciden con el cliente (hydration mismatch).
    if (typeof window === 'undefined') {
        resetIdCounter()
    }

    const Layout = Component.Layout || Noop
    const authRequired = Component.Protected || false
    const npState = useRouteProgressState()
    const [refreshKey, setRefreshKey] = useState(0)

    useEffect(() => {
        const handleRefresh = () => {
            setRefreshKey((prev) => prev + 1)
        }
        window.addEventListener('pylot:refresh-content', handleRefresh)
        return () => {
            window.removeEventListener('pylot:refresh-content', handleRefresh)
        }
    }, [])

    // Handle application errors
    useAppErrorRecovery()

    // New Relic Browser: SPA route tracking
    useNewRelic()

    // Libera bloqueos de scroll del body que sobreviven a un cambio de ruta
    useBodyScrollLockReset()

    useEffect(() => {
        if (smoothscroll) {
            smoothscroll.polyfill()
        }
        const removeLoading = () => document.body.classList?.remove('loading')
        // En la home el overlay sigue hasta que los banners terminan de cargar.
        if (router.pathname === '/') {
            const timeoutId = window.setTimeout(removeLoading, 8000)
            return () => window.clearTimeout(timeoutId)
        }
        removeLoading()
    }, [router.pathname])

    useMagentoStorefrontEvents()
    useSynonymDiscovery()
    useRegisterServiceWorker()

    // Snippet del contenedor: `_document` (head + noscript). Aquí solo el handler.
    const { gtmHandler } = useGTM(getGtmMappingOverrides())

    return (
        <ErrorBoundary>
            <DeviceProvider>
                <Head />
                <NanoProgress
                    isAnimating={npState.isRouteChanging}
                    key={npState.loadingKey}
                    // eslint-disable-next-line i18next/no-literal-string
                    backgroundColor="nano-progress"
                />
                <PopUpNewsFeatures />
                <TagContextInit handlers={[gtmHandler]}>
                    <ManagedUIContext LoadingIndicator={LoadingIndicator}>
                        <ManagedAuthContext>
                            <ModalStateProvider>
                                <CardInfoProvider>
                                    <Layout
                                        pageProps={pageProps}
                                        authRequired={authRequired}
                                    >
                                        <Toast />
                                        <GlobalModal />
                                        <Component
                                            {...pageProps}
                                            key={refreshKey}
                                        />
                                    </Layout>
                                </CardInfoProvider>
                            </ModalStateProvider>
                        </ManagedAuthContext>
                        <ScrollUpButton />
                    </ManagedUIContext>
                </TagContextInit>
            </DeviceProvider>
        </ErrorBoundary>
    )
}

export default appWithTranslation(MyApp, nextI18nextConfig)
